import { useEffect, useRef, useState, useCallback } from 'react';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '@/components/Loading/Spinner';
import {
  useBoxCoordinates,
  usePolygonPoints,
  useDenormalizePolygonPoints,
  usePolygonScalePoints,
} from '@/hooks/moderation.hooks';
import { ImageDimension } from '@/type/detection.types';

type ImgWithYoloBoxProps = {
  imgSrc: string;
  imageClassName?: string;
  rectangleClassName?: string;
  polygonClassName?: string;
  polygonThickness?: number;
  element?: TResultImageCoordinatesSchema;
  originalBoundingBox?: Array<number>;
  manyElementsAnnotations?: TResultImageCoordinatesSchema[];
  onImageLoading?: (loading: boolean) => void;
  onClick?: () => void;
};

export const ImgWithYoloBox = ({
  imgSrc,
  imageClassName,
  rectangleClassName,
  polygonClassName,
  polygonThickness = 5, // 2.5 on modal preview
  element,
  originalBoundingBox,
  manyElementsAnnotations,
  onImageLoading,
  onClick,
}: ImgWithYoloBoxProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [polygonImageUrl, setPolygonImageUrl] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageDimension, setImageDimension] = useState<ImageDimension | null>(
    null
  );

  const isModerated = element?.moderated;

  const { getCoordinates: getBoxCoordinates } = useBoxCoordinates({
    isModerated,
    element,
    imageDimension,
  });

  const box = getBoxCoordinates();

  const { getPoints: polygonPoints } = usePolygonPoints({ element });

  const { denormalizePoints: denormalizePolygonPoints } =
    useDenormalizePolygonPoints({
      originalBoundingBox,
      element,
    });

  const { getScalePoints: getPolygonScalePoints } = usePolygonScalePoints({
    isModerated,
    polygonPoints,
    denormalizePolygonPoints,
    imageDimension,
  });

  useEffect(() => {
    onImageLoading?.(isImageLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoading]);

  // Callback ref to handle image loading for both conditional image elements
  const setImgRef = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img) return;

      // Store the img reference
      imageRef.current = img;

      // Set loading to true when imgSrc changes
      setIsImageLoading(true);

      img.onload = () => {
        // Set loading to false when image has loaded
        setIsImageLoading(false);

        // Set width and height to actual image dimensions
        if (imageRef.current) {
          imageRef.current.width = img.naturalWidth;
          imageRef.current.height = img.naturalHeight;
          // Update image dimensions state
          setImageDimension({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }
      };

      // Handle case where image is already cached
      if (img.complete) {
        setIsImageLoading(false);

        // Set width and height to actual image dimensions for cached images
        img.width = img.naturalWidth;
        img.height = img.naturalHeight;
        // Update image dimensions state for cached images
        setImageDimension({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    },
    [imgSrc]
  );

  // Create a canvas with the polygon and generate a data URL
  useEffect(() => {
    if (!polygonPoints || polygonPoints.length < 3) {
      setPolygonImageUrl(null);
      return;
    }

    // Create a canvas element if it doesn't exist
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }

    const canvas = canvasRef.current;

    // Use a much larger canvas for better quality
    canvas.width = 1000;
    canvas.height = 1000;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create a completely transparent base
    ctx.fillStyle = 'rgba(255, 255, 255, 0)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scaledPoints = getPolygonScalePoints(canvas);

    // First: Draw the blue fill
    ctx.beginPath();
    ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);

    for (let i = 1; i < scaledPoints.length; i++) {
      ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
    }

    // Close the path
    ctx.closePath();

    // Fill with semi-transparent blue
    ctx.fillStyle = 'rgba(0, 0, 255, 0.1)';
    ctx.fill();

    // Second: Draw cleaner border lines
    // Anti-aliasing for smooth lines
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw red border
    ctx.beginPath();
    ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);

    for (let i = 1; i < scaledPoints.length; i++) {
      ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
    }

    // Close the polygon path
    ctx.lineTo(scaledPoints[0].x, scaledPoints[0].y);

    // Set line properties for clean rendering
    ctx.lineWidth = polygonThickness;
    ctx.strokeStyle = 'red';
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();

    // Generate data URL with PNG format for best quality
    try {
      const dataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality
      setPolygonImageUrl(dataUrl);
    } catch (error) {
      console.error('Error generating polygon image:', error);
      setPolygonImageUrl(null);
    }
  }, [polygonPoints, imageDimension, polygonThickness, getPolygonScalePoints]);

  // Convert normalized center-based coordinates to percentage-based top-left positioning
  const convertToBoxStyle = (modElement: TResultImageCoordinatesSchema) => {
    const left = (modElement.xCenter - modElement.width / 2) * 100;
    const top = (modElement.yCenter - modElement.height / 2) * 100;
    const width = modElement.width * 100;
    const height = modElement.height * 100;

    return {
      top: `${top}%`,
      left: `${left}%`,
      width: `${width}%`,
      height: `${height}%`,
    };
  };

  return (
    <div
      className="flex items-center justify-center h-full w-full"
      onClick={onClick}
    >
      <div className="relative inline-block text-[0px]">
        {/* If damaged, we'll show the polygon */}
        {element?.isDamaged && polygonPoints ? (
          <div className="relative">
            {/* Base image */}
            <img
              ref={setImgRef}
              src={imgSrc}
              alt="img"
              className={twMerge(
                'h-full w-full object-contain object-center',
                imageClassName,
                isImageLoading && 'opacity-0'
              )}
            />
            {/* Overlay the pre-rendered polygon image */}
            {polygonImageUrl && (
              <img
                src={polygonImageUrl}
                alt=""
                className={twMerge(
                  'absolute top-0 left-0 w-full h-full object-fill pointer-events-none',
                  polygonClassName,
                  isImageLoading && 'opacity-0'
                )}
              />
            )}
            {/* Render element annotation rectangles */}
            {manyElementsAnnotations?.map((modElement, index) => (
              <div
                key={`mod-${modElement.elNumber}-${index}`}
                className={twMerge(
                  'absolute border-[2.5px] border-red-FF0000 rounded-sm pointer-events-none',
                  rectangleClassName,
                  isImageLoading && 'opacity-0'
                )}
                style={convertToBoxStyle(modElement)}
              />
            ))}
          </div>
        ) : (
          <>
            <img
              ref={setImgRef}
              src={imgSrc}
              alt="img"
              className={twMerge(
                'h-full w-full object-contain object-center',
                imageClassName,
                isImageLoading && 'opacity-0'
              )}
            />
            {!element?.isDamaged && box && (
              <div
                className={twMerge(
                  'absolute border-[2.5px] border-red-FF0000 rounded-sm pointer-events-none',
                  rectangleClassName,
                  isImageLoading && 'opacity-0'
                )}
                style={{
                  top: `${box.y}%`,
                  left: `${box.x}%`,
                  width: `${box.width}%`,
                  height: `${box.height}%`,
                }}
              />
            )}
            {/* Render element annotation rectangles */}
            {manyElementsAnnotations?.map((modElement, index) => (
              <div
                key={`mod-${modElement.elNumber}-${index}`}
                className={twMerge(
                  'absolute border-[2.5px] border-red-FF0000 rounded-sm pointer-events-none',
                  rectangleClassName,
                  isImageLoading && 'opacity-0'
                )}
                style={convertToBoxStyle(modElement)}
              />
            ))}
          </>
        )}

        {isImageLoading && <Spinner />}
      </div>
    </div>
  );
};
